import { useCallback, useEffect, useState } from "react";

import { getErrorMessage, isAPIError } from "@/core/services";
import { getEmbarcacaoConsumoOperacao } from "./embarcacoes.gateway";
import { isEmbarcacaoConsumoOperacaoEmpty } from "./embarcacoes.types";
import type { EmbarcacaoConsumoOperacaoState } from "./embarcacoes.types";

export const CONSUMO_OPERACAO_SESSION_EXPIRED_MESSAGE =
  "Sua sessão expirou. Entre novamente para consultar os detalhes da embarcação.";
export const CONSUMO_OPERACAO_FORBIDDEN_MESSAGE =
  "Você não tem permissão para consultar os detalhes desta embarcação.";
export const CONSUMO_OPERACAO_NOT_FOUND_MESSAGE = "Embarcação não encontrada.";
export const CONSUMO_OPERACAO_ERROR_MESSAGE =
  "Não foi possível carregar o Consumo Modo Operação da embarcação.";

const INITIAL_STATE: EmbarcacaoConsumoOperacaoState = {
  status: "loading",
  consumo: null,
  errorMessage: null,
};

const NOT_FOUND_STATE: EmbarcacaoConsumoOperacaoState = {
  status: "notFound",
  consumo: null,
  errorMessage: CONSUMO_OPERACAO_NOT_FOUND_MESSAGE,
};

/** A seção existe, mas nenhum dos 8 campos tem valor (AC3). */
const EMPTY_STATE: EmbarcacaoConsumoOperacaoState = {
  status: "empty",
  consumo: null,
  errorMessage: null,
};

interface UseEmbarcacaoConsumoOperacaoResult extends EmbarcacaoConsumoOperacaoState {
  reload: () => void;
}

/**
 * Cada falha tem um estado próprio e nenhuma delas devolve campos da seção
 * (AC4); as demais seções do detalhamento seguem funcionando.
 */
function toFailure(error: unknown): EmbarcacaoConsumoOperacaoState {
  const status = isAPIError(error) ? error.status : null;

  if (status === 401) {
    return {
      status: "sessionExpired",
      consumo: null,
      errorMessage: CONSUMO_OPERACAO_SESSION_EXPIRED_MESSAGE,
    };
  }

  if (status === 403) {
    return {
      status: "forbidden",
      consumo: null,
      errorMessage: CONSUMO_OPERACAO_FORBIDDEN_MESSAGE,
    };
  }

  if (status === 404) return NOT_FOUND_STATE;

  return {
    status: "error",
    consumo: null,
    errorMessage: getErrorMessage(error) || CONSUMO_OPERACAO_ERROR_MESSAGE,
  };
}

/**
 * Consulta o Consumo Modo Operação da embarcação pedida. Somente leitura:
 * nenhuma escrita nem registro de auditoria na abertura da seção (AC5).
 */
export function useEmbarcacaoConsumoOperacao(id: string): UseEmbarcacaoConsumoOperacaoResult {
  const [state, setState] = useState<EmbarcacaoConsumoOperacaoState>(INITIAL_STATE);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    // Fechar o modal ou trocar de embarcação descarta a resposta em trânsito.
    const controller = new AbortController();

    setState(INITIAL_STATE);

    getEmbarcacaoConsumoOperacao({ id, signal: controller.signal })
      .then((consumo) => {
        if (cancelled) return;

        if (!consumo) {
          setState(NOT_FOUND_STATE);
          return;
        }

        setState(
          isEmbarcacaoConsumoOperacaoEmpty(consumo)
            ? EMPTY_STATE
            : { status: "success", consumo, errorMessage: null },
        );
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setState(toFailure(error));
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [id, reloadToken]);

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);

  return { ...state, reload };
}
